import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { USER_ROLE, UserRoleType } from '../types/user-role.types';
import { APP_ROUTES } from '../types/app-routes';
import { SelectionStateService } from './selection-state.service';
import { EVENT_ROLE, EventRole } from '../types/event-access.types';
import { rolePageTransition, slidePageAround } from '../utils/animations/role-switch.animations';

/** Raíz de la app: RedirectingComponent resuelve el destino según el rol. */
const ROOT_ROUTE = '/';

/** Pantalla del evento a la que lleva cada rol de entrada. */
const EVENT_ROUTE_BY_ROLE: Readonly<Record<EventRole, string>> = {
  [EVENT_ROLE.SELLER]: '/event-sales',
  [EVENT_ROLE.SALES_MANAGER]: APP_ROUTES.SALES_MANAGER_HOME,
  [EVENT_ROLE.ADMINISTRATOR]: APP_ROUTES.SALES_MANAGER_HOME,
  [EVENT_ROLE.GLOBAL_REPORT]: APP_ROUTES.SALES_MANAGER_HOME,
  [EVENT_ROLE.SCANNER]: '/scanner',
};

/** Comandos de router para entrar a un evento con un rol. */
export function eventCommandsFor(role: EventRole, eventId: number): string[] {
  return [EVENT_ROUTE_BY_ROLE[role], String(eventId)];
}

/** ¿La URL actual ya está dentro de la pantalla destino (incluidas sus tabs/subrutas)? */
function isUnderRoute(url: string, commands: readonly string[]): boolean {
  const target = commands.join('/');
  const path = url.split(/[?#]/)[0];
  return path === target || path.startsWith(`${target}/`);
}

@Injectable({
  providedIn: 'root'
})
export class RoleNavigationService {

  constructor(
    private _router: Router,
    private _selectionState: SelectionStateService,
    private _navCtrl: NavController,
  ) {}

  public navigateBasedOnRole(role: UserRoleType): void {
    this._router.navigate([this.homeRouteFor(role)]);
  }

  /** Entra a un evento: el rol activo pasa a ser el del botón tocado, solo para ese evento. */
  public async enterEvent(role: EventRole, eventId: number): Promise<void> {
    await this._selectionState.setEventContext(role, eventId);
    await this._navCtrl.navigateForward(eventCommandsFor(role, eventId), { animation: rolePageTransition });
  }

  /**
   * Cambia de rol dentro del evento (chip del header). Si el destino es la pantalla actual
   * (jefe ↔ admin ↔ reporte global) cambia el contexto, las tabs recargan y `page` hace un slide
   * para que se note el cambio; si no, navega con el mismo slide.
   */
  public async switchEventRole(role: EventRole, eventId: number, page: HTMLElement | null = null): Promise<void> {
    const sameContext = this._selectionState.selectedRole() === role
      && this._selectionState.activeEventId() === eventId;
    if (sameContext) {
      return;
    }
    const commands = eventCommandsFor(role, eventId);
    if (isUnderRoute(this._router.url, commands)) {
      await slidePageAround(page, () => this._selectionState.setEventContext(role, eventId));
      return;
    }
    await this._selectionState.setEventContext(role, eventId);
    await this._navCtrl.navigateForward(commands, { animation: rolePageTransition });
  }

  /** Destino post-login: los `sellers` siempre aterrizan en el dashboard del vendedor. */
  public landingRoute(): string {
    if (this._selectionState.isSellersUser()) {
      return APP_ROUTES.DASHBOARD_SELLER;
    }
    return this.homeRouteFor(this._selectionState.selectedRole());
  }

  public async navigateToLanding(): Promise<void> {
    await this._router.navigate([this.landingRoute()]);
  }

  public navigateToEventList(): void {
    this._navCtrl.navigateBack(APP_ROUTES.EVENTS_SELLER, { animation: rolePageTransition });
  }

  /**
   * Lleva al inicio del rol activo (fallback de ← cuando no hay historial), con animación hacia atrás.
   * Los `sellers` vuelven siempre al dashboard del vendedor.
   * Con el rol sin hidratar (recarga en frío) vuelve a '/', cuyo RedirectingComponent resuelve el destino por rol.
   */
  public navigateHome(): void {
    if (this._selectionState.isSellersUser()) {
      this._navCtrl.navigateBack(APP_ROUTES.DASHBOARD_SELLER);
      return;
    }
    const role = this._selectionState.selectedRole();
    if (role === USER_ROLE.UNKNOWN) {
      this._navCtrl.navigateBack(ROOT_ROUTE);
      return;
    }
    this._navCtrl.navigateBack(this.homeRouteFor(role));
  }

  private homeRouteFor(role: UserRoleType): string {
    switch (role) {
      case USER_ROLE.RECEPCIONIST:
      case USER_ROLE.SCANNER:
        return APP_ROUTES.EVENTS_RECEPCIONIST;
      case USER_ROLE.SELLER:
        return APP_ROUTES.EVENTS_SELLER;
      case USER_ROLE.SALES_MANAGER:
      case USER_ROLE.ADMINISTRATOR:
        return APP_ROUTES.SALES_MANAGER_HOME;
      case USER_ROLE.MOBILE_APP_ADMIN:
        return APP_ROUTES.APP_VERSION_ADMIN;
      default:
        return APP_ROUTES.NOT_FOUND;
    }
  }

  // TODO: borrar — reemplazada por roles por evento (2026-11): ya no hay links a /selection/role.
  /** @deprecated Sin llamadores: `/selection/role` quedó sin links con los roles por evento (2026-11). */
  public navigateToRoleSelection(): void {
    this._router.navigate([APP_ROUTES.SELECTION_ROLE]);
  }
}
